import React, { useState } from 'react';
import { Truck, User, Users, Eye } from 'lucide-react';
import { AuthPageShell, ChoiceScreen } from './AuthSubComponents';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Direct fetch for auth endpoints (login, register).
 * These MUST NOT go through api() because api() intercepts 401s
 * and tries token refresh, which corrupts the error handling.
 */
const authFetch = async (endpoint, body) => {
  let response;
  try {
    response = await fetch(`${API_BASE}/api${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    });
  } catch (e) {
    throw new Error('Network error — please check your connection');
  }
  
  let data = {};
  try { data = await response.json(); } catch (e) {}
  
  if (!response.ok) {
    // Map common status codes to user-friendly messages
    if (response.status === 401) {
      throw new Error(data.detail || 'Invalid email or password');
    }
    throw new Error(data.detail || data.message || `Error (${response.status})`);
  }
  
  return data;
};

const LoginPage = ({ onLogin }) => {
  const [mode, setMode] = useState('choice'); // choice, login, register-driver, register-business
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // Clear stale non-sensitive data before fresh login
      localStorage.removeItem('lawd_expires_at');
      localStorage.removeItem('lawd_user');
      
      const data = await authFetch('/auth/login', { email, password });
      // Tokens are now in httpOnly cookies — only store non-sensitive UI data
      if (data.expires_at) {
        localStorage.setItem('lawd_expires_at', String(data.expires_at));
      }
      if (data.user) {
        localStorage.setItem('lawd_user', JSON.stringify(data.user));
      }
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterBusiness = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      localStorage.removeItem('lawd_expires_at');
      localStorage.removeItem('lawd_user');
      
      const data = await authFetch('/auth/register', { 
        email, password, first_name: firstName, last_name: lastName, 
        company_name: companyName, phone 
      });
      if (data.expires_at) {
        localStorage.setItem('lawd_expires_at', String(data.expires_at));
      }
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterGroupView = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      localStorage.removeItem('lawd_expires_at');
      localStorage.removeItem('lawd_user');
      
      const data = await authFetch('/auth/register', { 
        email, password, first_name: firstName, last_name: lastName, 
        company_name: companyName, phone, role: 'GROUP_VIEWER'
      });
      if (data.expires_at) {
        localStorage.setItem('lawd_expires_at', String(data.expires_at));
      }
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterDriver = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      localStorage.removeItem('lawd_expires_at');
      localStorage.removeItem('lawd_user');
      
      const data = await authFetch('/owner-driver/register', { 
        email, password, first_name: firstName, last_name: lastName, phone 
      });
      if (data.expires_at) {
        localStorage.setItem('lawd_expires_at', String(data.expires_at));
      }
      if (data.user?.needs_onboarding) {
        localStorage.setItem('lawd_needs_onboarding', 'true');
      }
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setFirstName('');
    setLastName('');
    setCompanyName('');
    setPhone('');
    setError('');
  };

  const goBack = () => {
    resetForm();
    setMode('choice');
  };

  // Subscription Choice Screen
  if (mode === 'choice') {
    return <ChoiceScreen onSelectMode={setMode} />;
  }

  // Login Screen
  if (mode === 'login') {
    return (
      <AuthPageShell icon={Truck} iconBg="bg-emerald-500" title="LAWD" subtitle="Local Australian Work Diary" onBack={goBack} backTestId="login-back">
        <h2 className="text-lg font-bold text-white mb-6">Sign In</h2>

        <form onSubmit={handleLogin} className="space-y-4">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
            required
            data-testid="login-email"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
            required
            data-testid="login-password"
          />
          
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm" data-testid="auth-error">
              {error}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition disabled:opacity-50"
            data-testid="auth-submit-btn"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </AuthPageShell>
    );
  }

  // Owner/Driver Registration
  if (mode === 'register-driver') {
    return (
      <AuthPageShell icon={User} iconBg="bg-emerald-500" title="Owner/Driver" subtitle="Solo operator registration" onBack={goBack} backTestId="register-driver-back">
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 mb-6">
          <div className="text-sm text-emerald-200">
            <strong>Owner/Driver Plan:</strong> Perfect for solo operators. You'll manage your own vehicle, diary, and compliance.
          </div>
        </div>

        <form onSubmit={handleRegisterDriver} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
              required
              data-testid="register-first-name"
            />
            <input
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
              required
              data-testid="register-last-name"
            />
          </div>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
            required
            data-testid="register-email"
          />
          <input
            type="tel"
            placeholder="Phone (optional)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
            data-testid="register-phone"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
            required
            minLength={6}
            data-testid="register-password"
          />
          
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm" data-testid="auth-error">
              {error}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition disabled:opacity-50"
            data-testid="auth-submit-btn"
          >
            {loading ? 'Creating account...' : 'Start 7-Day Free Trial'}
          </button>
        </form>

        <p className="text-center text-slate-500 text-xs mt-4">
          No credit card required. You'll add your vehicle and license details next.
        </p>
      </AuthPageShell>
    );
  }

  // Business/Fleet Registration
  if (mode === 'register-business') {
    return (
      <AuthPageShell icon={Users} iconBg="bg-blue-500" title="Business" subtitle="Fleet management registration" onBack={goBack} backTestId="register-business-back">
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 mb-6">
          <div className="text-sm text-blue-200">
            <strong>Business Plan:</strong> Full fleet management with multiple drivers, vehicles, scheduling, and record keeping.
          </div>
        </div>

        <form onSubmit={handleRegisterBusiness} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
              required
              data-testid="register-first-name"
            />
            <input
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
              required
              data-testid="register-last-name"
            />
          </div>
          <input
            type="text"
            placeholder="Company Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
            required
            data-testid="register-company"
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
            required
            data-testid="register-email"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
            required
            minLength={6}
            data-testid="register-password"
          />
          
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm" data-testid="auth-error">
              {error}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition disabled:opacity-50"
            data-testid="auth-submit-btn"
          >
            {loading ? 'Creating account...' : 'Start 7-Day Free Trial'}
          </button>
        </form>

        <p className="text-center text-slate-500 text-xs mt-4">
          No credit card required. Add drivers and vehicles after setup.
        </p>
      </AuthPageShell>
    );
  }

  // Group View Registration
  if (mode === 'register-group-view') {
    return (
      <AuthPageShell icon={Eye} iconBg="bg-violet-500" title="Group View" subtitle="View consented drivers across fleets" onBack={goBack} backTestId="register-gv-back">
        <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-3 mb-6">
          <div className="text-sm text-violet-200">
            <strong>Group View:</strong> Monitor consented drivers from owner/operators and business fleets. Request access, receive alerts, and track compliance across multiple sources.
          </div>
        </div>

        <form onSubmit={handleRegisterGroupView} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-violet-500"
              required
              data-testid="register-first-name"
            />
            <input
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-violet-500"
              required
              data-testid="register-last-name"
            />
          </div>
          <input
            type="text"
            placeholder="Company / Organisation Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-violet-500"
            required
            data-testid="register-company"
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-violet-500"
            required
            data-testid="register-email"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-violet-500"
            required
            minLength={6}
            data-testid="register-password"
          />
          
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm" data-testid="auth-error">
              {error}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-violet-500 hover:bg-violet-600 text-white font-medium rounded-lg transition disabled:opacity-50"
            data-testid="auth-submit-btn"
          >
            {loading ? 'Creating account...' : 'Create Group View Account'}
          </button>
        </form>

        <p className="text-center text-slate-500 text-xs mt-4">
          After setup, send share requests to drivers or businesses for consent.
        </p>
      </AuthPageShell>
    );
  }

  return null;
};

export default LoginPage;
